// src/components/Browser/AuthenticationModal.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import { FaShieldAlt, FaExclamationTriangle, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { WalletContext } from '../../contexts/WalletContext';
import FaviconImage from './FaviconImage';
import './AuthenticationModal.css';

const AuthenticationModal = ({
  isOpen,
  onClose,
  authRequest,
  onApprove,
  onReject,
  isLoading,
  site,
  securityInfo
}) => {
  const { t } = useTranslation();
  const { currentWallet } = useContext(WalletContext);
  const [expanded, setExpanded] = useState(false);
  const [formattedMessage, setFormattedMessage] = useState('');
  
  // Process authentication request when it changes
  useEffect(() => {
    if (!authRequest) return;
    
    try {
      // Format the authentication message for display
      let message;
      
      // Check different possible message locations
      if (authRequest.params && authRequest.params.message) {
        message = authRequest.params.message;
      } 
      else if (authRequest.params && authRequest.params.request && authRequest.params.request.params) {
        // Sometimes the message is nested in request.params
        message = authRequest.params.request.params.message || 
                (Array.isArray(authRequest.params.request.params) ? 
                  authRequest.params.request.params[0] : undefined);
      }
      else if (authRequest.message) {
        message = authRequest.message;
      }
      else if (Array.isArray(authRequest.params)) {
        // For some authentication requests, the message might be in the params array
        message = authRequest.params[0];
      }
      
      // If we still couldn't find the message, use a default placeholder
      if (!message) {
        message = t('browser.authRequest.defaultMessage', 'Authentication message not provided');
      }
      
      // If message is a hex string, convert it to readable text
      if (typeof message === 'string' && ethers.isHexString(message)) {
        try {
          const decoded = ethers.toUtf8String(message);
          setFormattedMessage(decoded);
        } catch (error) {
          console.error('Error decoding hex message:', error);
          setFormattedMessage(message);
        }
      } else if (typeof message === 'object') {
        // If it's an object, stringify it for display
        setFormattedMessage(JSON.stringify(message, null, 2));
      } else {
        // Otherwise use as is
        setFormattedMessage(message);
      }
    } catch (error) {
      console.error('Error processing authentication request:', error);
      setFormattedMessage(t('browser.authRequest.errorProcessing'));
    }
  }, [authRequest, t]);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onReject();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onReject]);
  
  // Render security status
  const renderSecurityStatus = () => {
    if (!securityInfo) return null;
    
    const { validation, isScam } = securityInfo;
    
    if (isScam) {
      return (
        <div className="auth-warning danger">
          <FaExclamationTriangle className="warning-icon danger" />
          <p>{t('browser.authRequest.securityThreat', 'This domain is flagged as malicious! Signing may put your funds at risk.')}</p>
        </div>
      );
    }
    
    switch(validation) {
      case 'VALID':
        return (
          <div className="auth-security valid">
            <FaCheckCircle className="security-icon" />
            <p>{t('browser.authRequest.verifiedDomain', 'Domain verified')}</p>
          </div>
        );
      case 'INVALID':
        return (
          <div className="auth-warning">
            <FaExclamationTriangle className="warning-icon" />
            <p>{t('browser.authRequest.domainMismatch', 'Domain mismatch detected. Be cautious!')}</p>
          </div>
        );
      case 'UNKNOWN':
      default:
        return (
          <div className="auth-info">
            <FaInfoCircle className="info-icon" />
            <p>{t('browser.authRequest.unverifiedDomain', 'Unverified domain. Proceed with caution.')}</p>
          </div>
        );
    }
  };
  
  const getHostname = () => {
    try {
      return new URL(site).hostname;
    } catch (e) {
      return site;
    }
  };
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  // Handle modal click to prevent overlay click propagation
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  if (!isOpen || !authRequest) return null;
  
  return (
    <div className="auth-modal-overlay" onClick={onReject}>
      <div className="auth-modal" onClick={handleModalClick}>
        <div className="auth-modal-header">
          <FaShieldAlt className="auth-icon" />
          <h2>{t('browser.authRequest.title', 'Sign-in Request')}</h2>
          <button className="close-button" onClick={onReject}>×</button>
        </div>
        
        <div className="auth-modal-content">
          <div className="site-info">
            <div className="site-favicon">
              <FaviconImage hostname={getHostname()} className="site-favicon-img" alt={getHostname()} />
            </div>
            <div className="site-details">
              <h3>{getHostname()}</h3>
              <p className="site-url">{site}</p>
            </div>
          </div>
          
          <div className="auth-warning">
            <FaExclamationTriangle className="warning-icon" />
            <p>{t('browser.authRequest.warning', 'You are about to sign in to this site with your Ethereum account')}</p>
          </div>
          
          {renderSecurityStatus()}
          
          <div className="wallet-info">
            <h4>{t('browser.authRequest.signingWith', 'Signing with')}</h4>
            <div className="wallet-address">
              <span className="address-label">{currentWallet?.name || t('wallet.account', { number: 1 })}</span>
              <span className="address">{formatAddress(currentWallet?.address)}</span>
            </div>
          </div>
          
          <div className="auth-message-container">
            <div className="auth-message-header">
              <span className="detail-label">{t('browser.authRequest.message', 'Sign-in Message')}</span>
              <button 
                className="expand-button" 
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? t('browser.authRequest.collapse', 'Collapse') : t('browser.authRequest.expand', 'Expand')}
              </button>
            </div>
            <pre className={`auth-message ${expanded ? 'expanded' : ''}`}>
              {formattedMessage}
            </pre>
          </div>
          
          <div className="auth-info">
            <FaInfoCircle className="info-icon" />
            <p>{t('browser.authRequest.signingInfo', 'By signing, you will authenticate with this site. This is not a transaction and does not cost gas.')}</p>
          </div>
          
          <div className="auth-actions">
            <button 
              className="reject-button"
              onClick={onReject}
              disabled={isLoading}
            >
              {t('browser.authRequest.reject', 'Reject')}
            </button>
            <button 
              className="approve-button"
              onClick={onApprove}
              disabled={isLoading || (securityInfo && securityInfo.isScam)}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : null}
              {t('browser.authRequest.sign', 'Sign In')}
            </button>
          </div>
          
          {securityInfo && securityInfo.isScam && (
            <div className="security-warning">
              <FaExclamationTriangle className="warning-icon large" />
              <p>{t('browser.authRequest.maliciousWarning', 
                'Authentication blocked. This site has been flagged as malicious by security providers.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthenticationModal;